import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { groupBy } from 'lodash';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { LD_PROJECT_TRASH_CAN_FLAG } from 'constants/featureFlags';
import Steven, { ELEMENT_TYPES } from 'helpers/steven';

import { useAvatarData, usePhotoOptimisticEdits } from 'javascripts/hooks';

import useMenuActionDownload from 'components/assetFeed/actions/useMenuActionDownload';
import useMenuActionTag from 'components/assetFeed/actions/useMenuActionTag';
import useMenuActionDuplicate from 'components/assetFeed/actions/useMenuActionDuplicate';
import useMenuActionShareSingle from 'components/assetFeed/actions/useMenuActionShareSingle';
import useMenuActionSetCoverPhoto from 'components/assetFeed/actions/useMenuActionSetCoverPhoto';
import useMenuActionDelete from 'components/assetFeed/actions/useMenuActionDelete';
import { CardMenu, CardMenuItem } from 'components/shared';
import AssetThumbnail from 'components/shared/DesignSystem/AssetThumbnail';

import Common from 'helpers/intl-common';

function AssetFeedItem({
  asset,
  assetSize,
  selected,
  onClick,
  toggleSelected,
  enableSelection,
  enableMenu,
}) {
  const assets = [asset];
  const assetsByTypename = groupBy(assets, '__typename');
  const menuItemOptions = {
    assets,
    assetsByType: {
      videos: assetsByTypename['Video'] || [],
      photos: assetsByTypename['Photo'] || [],
    },
  };

  const flags = useFlags();
  const isTrashCanEnabled = flags[LD_PROJECT_TRASH_CAN_FLAG];

  const [actionDownload, renderModalDownload] =
    useMenuActionDownload(menuItemOptions);
  const [actionTag, renderModalTag] = useMenuActionTag(menuItemOptions);
  const [actionDuplicate] = useMenuActionDuplicate(menuItemOptions);
  const [actionSetCoverPhoto] = useMenuActionSetCoverPhoto(menuItemOptions);
  const [actionShareSingle, renderModalShareSingle] =
    useMenuActionShareSingle(menuItemOptions);
  const [actionDelete] = useMenuActionDelete(menuItemOptions);

  const { isExternal, color } = useAvatarData(asset);

  const { getOptimisticUri } = usePhotoOptimisticEdits();

  const renderCardMenu = () => {
    if (selected || !enableMenu) {
      return null;
    }
    return (
      <>
        {renderModalDownload()}
        {renderModalShareSingle()}
        {renderModalTag()}
        <CardMenu triggerWrapperClass="zoom" size="medium">
          <CardMenuItem
            onClick={actionDownload}
            data-testid="project__item__more-menu__download"
          >
            <React.Fragment>
              <i className="mdi mdi-cloud-download-outline" />
              <FormattedMessage {...Common.download} />
            </React.Fragment>
          </CardMenuItem>
          <CardMenuItem
            onClick={actionDuplicate}
            data-testid="project__item__more-menu__duplicate"
          >
            <React.Fragment>
              <i className="mdi mdi-image-multiple-outline" />
              <FormattedMessage id="Assets.selectedAssetBar.duplicate" />
            </React.Fragment>
          </CardMenuItem>
          <CardMenuItem
            onClick={actionSetCoverPhoto}
            data-testid="project__item__more-menu__set-as-cover-photo"
          >
            <React.Fragment>
              <i className="mdi mdi-image-frame" />
              <FormattedMessage id="Assets.selectedAssetBar.setCoverPhoto" />
            </React.Fragment>
          </CardMenuItem>
          <CardMenuItem
            onClick={actionShareSingle}
            data-testid="project__item__more-menu__share"
          >
            <React.Fragment>
              <i className="mdi mdi-send-outline" />
              <FormattedMessage {...Common.share} />
            </React.Fragment>
          </CardMenuItem>
          <CardMenuItem
            onClick={actionTag}
            data-testid="project__item__more-menu__tag"
          >
            <React.Fragment>
              <i className="mdi mdi-tag-multiple-outline" />
              <FormattedMessage id="Assets.selectedAssetBar.tag" />
            </React.Fragment>
          </CardMenuItem>
          <hr />
          {actionDelete ? (
            <CardMenuItem
              data-testid="projects__actions__delete-photo-link"
              onClick={() => {
                actionDelete();
                Steven.elementClicked({
                  id: 'projects_photos_three_dots_menu_trashcan',
                  content:
                    'User deletes photo by trash can from three dots menu',
                  type: ELEMENT_TYPES.BUTTON,
                  action: 'projects_delete_photo_by_three_dots_menu_trashcan',
                });
              }}
            >
              <React.Fragment>
                <i className="mdi mdi-delete-outline" />
                {isTrashCanEnabled ? (
                  <FormattedMessage {...Common.moveToTrash} />
                ) : (
                  <FormattedMessage {...Common.delete} />
                )}
              </React.Fragment>
            </CardMenuItem>
          ) : null}
        </CardMenu>
      </>
    );
  };

  const thumbnailSource =
    getOptimisticUri(asset.databaseId) || asset.assetPreviewThumbnail;

  return (
    <AssetThumbnail
      src={thumbnailSource}
      creatorURI={asset.contributor.avatar}
      creatorName={asset.contributor.name}
      isVideo={asset.__typename === 'Video'}
      hasComments={asset.commentsCount > 0 || asset.voiceNotesCount > 0}
      hasDescription={asset.hasDescription}
      hasTags={asset.tagsCount > 0}
      internal={asset.internal}
      selected={selected}
      toggleSelected={(event) => {
        toggleSelected(asset, event.shiftKey);
      }}
      enableSelection={enableSelection}
      onClick={onClick}
      avatarExternal={isExternal}
      avatarColor={color}
      assetSize={assetSize}
    >
      {renderCardMenu()}
    </AssetThumbnail>
  );
}

AssetFeedItem.propTypes = {
  asset: PropTypes.object,
  assetSize: PropTypes.string,
  selected: PropTypes.bool,
  onClick: PropTypes.func,
  toggleSelected: PropTypes.func,
  enableSelection: PropTypes.bool,
  enableMenu: PropTypes.bool,
};

AssetFeedItem.defaultProps = {
  enableSelection: true,
  enableMenu: true,
};

export default AssetFeedItem;
